import Custom404 from "@dust-tt/front/components/pages/Custom404";
import { GlobalErrorFallback } from "@spa/app/components/GlobalErrorFallback";
import { AppContentRouterLayout } from "@spa/app/layouts/AppContentRouterLayout";
import { RootRouterLayout } from "@spa/app/layouts/RootRouterLayout";
import { UnauthenticatedPage } from "@spa/app/layouts/UnauthenticatedPage";
import { WorkspacePage } from "@spa/app/layouts/WorkspacePage";
import { IndexPage } from "@spa/app/pages/IndexPage";
import { adminFullPageRoutes, adminRoutes } from "@spa/app/routes/adminRoutes";
import { appsRoutes } from "@spa/app/routes/appsRoutes";
import {
  builderContentRoutes,
  builderFullPageRoutes,
  builderRedirectRoutes,
} from "@spa/app/routes/builderRoutes";
import {
  conversationRedirectRoutes,
  conversationRoutes,
} from "@spa/app/routes/conversationRoutes";
import { labsRoutes } from "@spa/app/routes/labsRoutes";
import {
  loginAuthenticatedRoutes,
  loginUnauthenticatedRoutes,
} from "@spa/app/routes/loginRoutes";
import { onboardingRoutes } from "@spa/app/routes/onboardingRoutes";
import { podsRoutes } from "@spa/app/routes/podsRoutes";
import {
  spacesRedirectRoutes,
  spacesRoutes,
} from "@spa/app/routes/spacesRoutes";
import { withSuspense } from "@spa/app/routes/withSuspense";
import type { RouteObject } from "react-router-dom";
import { useLocation } from "react-router-dom";

const MaintenancePage = withSuspense(
  () => import("@dust-tt/front/components/pages/MaintenancePage"),
  "MaintenancePage"
);

// Sub-app pages (oauth / share / email), also mounted on the main router.
// The dedicated sub-app HTML shells exist for the Cloudflare Worker
// topology (front-spa/worker/app.ts maps their paths to per-sub-app
// entries), but the live production shape is plain static hosting with an
// SPA fallback (Hostinger static, hand-uploaded dist) — there, every path
// serves the MAIN index.html and only this router decides what renders.
// Without these routes, /w/:wId/oauth/*, /share/* and /email/* hit the
// catch-all 404. Lazy-loaded: the main bundle does not pay for them.
const OAuthSetupRedirectPage = withSuspense(
  () => import("@dust-tt/front/components/pages/oauth/OAuthSetupRedirectPage"),
  "OAuthSetupRedirectPage"
);
const OAuthFinalizePage = withSuspense(
  () => import("@dust-tt/front/components/pages/oauth/OAuthFinalizePage"),
  "OAuthFinalizePage"
);
const SharedFramePage = withSuspense(
  () => import("@dust-tt/front/components/pages/share/SharedFramePage"),
  "SharedFramePage"
);
const SharedFilePage = withSuspense(
  () => import("@dust-tt/front/components/pages/share/SharedFilePage"),
  "SharedFilePage"
);
const ShareOgPage = withSuspense(
  () => import("@dust-tt/front/components/pages/share/ShareOgPage"),
  "ShareOgPage"
);
const ValidationPage = withSuspense(
  () => import("@dust-tt/front/components/pages/email/ValidationPage"),
  "ValidationPage"
);

// Redirect /poke/* to the poke app (poke.dust.tt)
function PokeRedirect() {
  const location = useLocation();
  const pokePath = location.pathname.replace(/^\/poke/, "");
  const pokeOrigin = window.location.origin.replace("://app.", "://poke.");
  window.location.replace(
    `${pokeOrigin}${pokePath}${location.search}${location.hash}`
  );
  return null;
}

export const routes: RouteObject[] = [
  {
    element: <RootRouterLayout />,
    errorElement: <GlobalErrorFallback />,
    children: [
      { path: "/", element: <IndexPage /> },
      {
        path: "/w/:wId",
        element: <WorkspacePage />,
        children: [
          // Routes WITH shared AppContentLayout (navigation, sidebar, title bar)
          {
            element: <AppContentRouterLayout />,
            children: [
              ...conversationRoutes,
              ...adminRoutes,
              ...labsRoutes,
              ...spacesRoutes,
              ...appsRoutes,
              ...builderContentRoutes,
              ...spacesRedirectRoutes,
              ...podsRoutes,
            ],
          },

          // Routes WITHOUT AppContentLayout (no sidebar/navigation chrome)
          ...adminFullPageRoutes,
          ...builderFullPageRoutes,
          ...builderRedirectRoutes,
          ...conversationRedirectRoutes,
          ...onboardingRoutes,
        ],
      },
      // Login (authenticated routes + logout)
      ...loginAuthenticatedRoutes,
      // Redirect /poke/* to the poke app (e.g., poke.dust.tt)
      { path: "/poke/*", element: <PokeRedirect /> },
      // Login (unauthenticated routes)
      ...loginUnauthenticatedRoutes,
      // Sub-app routes (see the lazy declarations above for why the main
      // router must carry them). Paths mirror the sub-apps' own routers;
      // /email/* is a splat because the email sub-app renders its single
      // validation page for every path it is served under.
      {
        path: "/w/:wId/oauth/:provider/setup",
        element: <OAuthSetupRedirectPage />,
      },
      { path: "/oauth/:provider/finalize", element: <OAuthFinalizePage /> },
      { path: "/share/frame/:token", element: <SharedFramePage /> },
      { path: "/share/file/:token", element: <SharedFilePage /> },
      { path: "/share/og/:wId", element: <ShareOgPage /> },
      { path: "/email/*", element: <ValidationPage /> },
      // Global catch-all routes
      {
        element: <UnauthenticatedPage />,
        children: [
          { path: "/maintenance", element: <MaintenancePage /> },
          { path: "*", element: <Custom404 /> },
        ],
      },
    ],
  },
];
